import React from 'react';
import { Box, Typography, Button, Skeleton, Alert, Stack } from '@mui/material';
import {
    Notifications as BellIcon,
    ShoppingBag as OrderIcon,
    LocalShipping as ShipIcon,
    CheckCircle as DeliveredIcon,
    Cancel as CancelIcon,
    Info as ConfirmedIcon,
    MarkEmailRead as MarkAllIcon,
    Circle as DotIcon,
    ArrowForwardIos as ChevronIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import marketplaceService from '../services/marketplaceService';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
    blue:      '#1976d2',
    blueLight: '#e3f0fc',
    blueDark:  '#1256a0',
    bg:        '#f5f7fa',
    white:     '#ffffff',
    border:    '#e0e4ea',
    text:      '#1a1d23',
    muted:     '#6b7280',
    radius:    '6px',
};

const TYPE_META = {
    new_order:       { icon: <OrderIcon />,     iconBg: '#e3f0fc', iconColor: '#1976d2', label: 'New Order' },
    order_confirmed: { icon: <ConfirmedIcon />, iconBg: '#e3f0fc', iconColor: '#1565c0', label: 'Confirmed' },
    order_shipped:   { icon: <ShipIcon />,      iconBg: '#f3e5f5', iconColor: '#7b1fa2', label: 'Shipped' },
    order_delivered: { icon: <DeliveredIcon />, iconBg: '#e8f5e9', iconColor: '#2e7d32', label: 'Delivered' },
    order_cancelled: { icon: <CancelIcon />,    iconBg: '#fdecea', iconColor: '#c62828', label: 'Cancelled' },
};

const DEFAULT_META = { icon: <BellIcon />, iconBg: C.blueLight, iconColor: C.blue, label: 'Notification' };

const SELLER_TYPES = new Set(['new_order', 'order_received', 'new_sale', 'sale']);

const formatTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHrs / 24);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHrs < 24) return `${diffHrs}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ─── Notification row ─────────────────────────────────────────────────────────
const NotifRow = ({ n, onClick, isLast }) => {
    const meta = TYPE_META[n.type] || DEFAULT_META;
    return (
        <Box
            onClick={() => onClick(n)}
            sx={{
                display: 'flex', alignItems: 'center', gap: 2,
                px: 3, py: 2, cursor: 'pointer',
                bgcolor: n.is_read ? C.white : C.blueLight + '55',
                borderBottom: isLast ? 'none' : `1px solid ${C.border}`,
                transition: 'background 0.12s',
                '&:hover': { bgcolor: C.bg },
            }}
        >
            {/* Icon */}
            <Box sx={{
                width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                bgcolor: meta.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {React.cloneElement(meta.icon, { sx: { fontSize: 18, color: meta.iconColor } })}
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                    {!n.is_read && (
                        <DotIcon sx={{ fontSize: 8, color: C.blue, flexShrink: 0 }} />
                    )}
                    <Typography sx={{
                        fontSize: 13, fontWeight: n.is_read ? 400 : 600,
                        color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {n.message}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box component="span" sx={{
                        fontSize: 11, fontWeight: 600, px: 1, py: '1px', borderRadius: '4px',
                        bgcolor: meta.iconBg, color: meta.iconColor,
                    }}>
                        {meta.label}
                    </Box>
                    <Typography sx={{ fontSize: 11, color: C.muted }}>{formatTime(n.created_at)}</Typography>
                </Box>
            </Box>

            {/* Chevron */}
            {n.order_id && (
                <ChevronIcon sx={{ fontSize: 12, color: C.muted, flexShrink: 0 }} />
            )}
        </Box>
    );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const NotificationsPage = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { data: notifications, isLoading, isError } = useQuery({
        queryKey: ['notifications'],
        queryFn: () => marketplaceService.getNotifications({ limit: 50 }),
        staleTime: 30 * 1000,
    });

    const markReadMutation = useMutation({
        mutationFn: (id) => marketplaceService.markNotificationRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries(['notifications']);
            queryClient.invalidateQueries(['notificationCount']);
        },
    });

    const markAllMutation = useMutation({
        mutationFn: marketplaceService.markAllNotificationsRead,
        onSuccess: () => {
            queryClient.invalidateQueries(['notifications']);
            queryClient.invalidateQueries(['notificationCount']);
        },
    });

    const handleClick = (n) => {
        if (!n.is_read) markReadMutation.mutate(n.id);
        if (!n.order_id) return;
        if (SELLER_TYPES.has(n.type)) {
            navigate('/my-shop');
        } else {
            navigate('/orders');
        }
    };

    const unreadCount = notifications?.filter((n) => !n.is_read).length ?? 0;
    const todayNotifs = notifications?.filter((n) => {
        const d = new Date(n.created_at);
        const now = new Date();
        return d.toDateString() === now.toDateString();
    }) || [];
    const olderNotifs = notifications?.filter((n) => {
        const d = new Date(n.created_at);
        const now = new Date();
        return d.toDateString() !== now.toDateString();
    }) || [];

    return (
        <Box sx={{ bgcolor: C.bg, minHeight: '100vh' }}>
            {/* Page header */}
            <Box sx={{ bgcolor: C.white, borderBottom: `1px solid ${C.border}`, px: 4, py: 2.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: C.radius,
                            bgcolor: C.blue, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <BellIcon sx={{ fontSize: 20, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>
                                    Notifications
                                </Typography>
                                {unreadCount > 0 && (
                                    <Box sx={{
                                        px: 1, py: '1px', borderRadius: '20px', bgcolor: C.blue,
                                        fontSize: 11, fontWeight: 700, color: '#fff',
                                    }}>
                                        {unreadCount}
                                    </Box>
                                )}
                            </Stack>
                            <Typography sx={{ fontSize: 12, color: C.muted }}>
                                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                            </Typography>
                        </Box>
                    </Stack>

                    {unreadCount > 0 && (
                        <Button
                            startIcon={<MarkAllIcon sx={{ fontSize: 16 }} />}
                            onClick={() => markAllMutation.mutate()}
                            disabled={markAllMutation.isPending}
                            sx={{
                                fontSize: 13, textTransform: 'none', fontWeight: 600,
                                color: C.blue, border: `1px solid ${C.border}`,
                                borderRadius: C.radius, px: 2,
                                '&:hover': { bgcolor: C.blueLight, borderColor: C.blue },
                            }}
                        >
                            Mark all read
                        </Button>
                    )}
                </Stack>
            </Box>

            {/* Content */}
            <Box sx={{ bgcolor: C.white }}>
                {isError && (
                    <Box sx={{ p: 3 }}>
                        <Alert severity="error">Failed to load notifications</Alert>
                    </Box>
                )}

                {isLoading && (
                    <Box>
                        {[...Array(6)].map((_, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2, borderBottom: `1px solid ${C.border}` }}>
                                <Skeleton variant="circular" width={38} height={38} />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton variant="text" width="60%" height={16} sx={{ mb: 0.5 }} />
                                    <Skeleton variant="text" width="30%" height={13} />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                {!isLoading && notifications?.length === 0 && (
                    <Box sx={{ py: 8, textAlign: 'center' }}>
                        <Box sx={{
                            width: 64, height: 64, borderRadius: '50%', bgcolor: C.blueLight,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2,
                        }}>
                            <BellIcon sx={{ fontSize: 30, color: C.blue }} />
                        </Box>
                        <Typography sx={{ fontSize: 15, fontWeight: 600, color: C.text, mb: 0.5 }}>
                            No notifications yet
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: C.muted }}>
                            You'll be notified about orders and activity here.
                        </Typography>
                    </Box>
                )}

                {!isLoading && notifications?.length > 0 && (
                    <>
                        {todayNotifs.length > 0 && (
                            <>
                                <Box sx={{ px: 3, py: 1.2, bgcolor: C.bg, borderBottom: `1px solid ${C.border}` }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                        Today
                                    </Typography>
                                </Box>
                                {todayNotifs.map((n, idx) => (
                                    <NotifRow key={n.id} n={n} onClick={handleClick}
                                        isLast={idx === todayNotifs.length - 1 && olderNotifs.length === 0} />
                                ))}
                            </>
                        )}

                        {olderNotifs.length > 0 && (
                            <>
                                <Box sx={{ px: 3, py: 1.2, bgcolor: C.bg, borderBottom: `1px solid ${C.border}` }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                        Earlier
                                    </Typography>
                                </Box>
                                {olderNotifs.map((n, idx) => (
                                    <NotifRow key={n.id} n={n} onClick={handleClick}
                                        isLast={idx === olderNotifs.length - 1} />
                                ))}
                            </>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default NotificationsPage;
